import React from 'react';
import { filter, sortBy } from 'lodash';

import { default as AnswerLanguages } from '@ad-scripts/domain/models/languages/answer_languages';
import { Delete } from 'src/presentation/components/pages/shared/rowActions/Delete';
import { Edit } from 'src/presentation/components/pages/shared/rowActions/Edit';
import ExternalLink from 'src/presentation/components/pages/shared/ExternalLink';
import KeywordGroupEditor from './KeywordGroupEditor';
import KeywordGroupsEmptyState from './KeywordGroupsEmptyState';
import keywordGroupsResource from 'src/actions/keywordGroups/keywordGroupsResource';
import RouterLinkButton from 'src/presentation/components/RouterLinkButton';
import TablePage, { TablePageRender } from 'src/presentation/components/pages/shared/TablePage';
import useResource from 'src/presentation/hooks/useResource';
import { CHAT_BRAND } from 'src/constants/branding';

const description = (
  <span>
    Keyword Groups help agents and customers find relevant answers in the Answers panel, {CHAT_BRAND.name}, and Help
    Center. Searching for any keyword in the group will also search for answers containing other keywords in the same
    group. Learn more about managing keyword groups in our{' '}
    <ExternalLink data-aid="learn-about-route-groups" to="https://help.gladly.com/docs/what-are-keyword-groups" />.
  </span>
);

const columnConfig = [
  { attr: 'label', label: 'Group Name' },
  { attr: 'phrases', label: 'Keywords', customRenderer: phrasesToValue },
  { attr: 'languages', label: 'Languages', customRenderer: languagesToValue },
  { attr: 'lastModified', label: 'Last Modified' },
];

function languagesToValue(keywordGroup) {
  const languageArr = keywordGroup.languages;
  if (!languageArr || !languageArr.length) return 'All Languages';
  return languageArr.map(l => AnswerLanguages[l]).join(', ');
}

function phrasesToValue(keywordGroup) {
  return keywordGroup.phrases ? keywordGroup.phrases.join(', ') : 'No keywords';
}

function renderRowActions({ item }) {
  return (
    <>
      <Edit id={item.id} />
      <Delete
        confirmationText="Permanently remove this keyword group?"
        deleteAction={keywordGroupsResource.effects.remove}
        id={item.id}
        submitText="Delete Keyword Group"
      >
        <div>
          Deleting this keyword group will stop agents and customers from viewing it. <br />
          This action cannot be undone.
        </div>
      </Delete>
    </>
  );
}

const KeywordGroups: TablePageRender = ({ root }) => {
  const { errors, loadingState, values } = useResource(keywordGroupsResource);

  function filterFunc(items, searchString) {
    if (!searchString) {
      return items;
    }
    const lowerCaseSearchText = searchString.toLowerCase();
    return filter(items, item => {
      return (
        (item.label && item.label.toLowerCase().indexOf(lowerCaseSearchText) > -1) ||
        phrasesToValue(item.phrases).toLowerCase().indexOf(lowerCaseSearchText) > -1
      );
    });
  }

  const keywordGroups = sortBy(
    values.map(v => {
      const lastModified = new Date(v.updatedAt).toLocaleDateString();
      return { ...v, lastModified };
    }),
    v => v.label.toLowerCase()
  );

  const actions = (
    <RouterLinkButton data-aid="admin-keyword-groups-addNew" to={`${root}/new`}>
      Create Keyword Group
    </RouterLinkButton>
  );

  // treat unstarted as loading so as to not flash the empty state before we fetch
  const isLoading = loadingState === 'loading' || loadingState === 'unstarted';

  return (
    <TablePage
      actions={actions}
      columnConfig={columnConfig}
      description={description}
      editor={KeywordGroupEditor}
      emptyState={KeywordGroupsEmptyState}
      errors={errors}
      filterFunc={filterFunc}
      isLoading={isLoading}
      items={keywordGroups}
      name="Keyword Groups"
      resetErrors={keywordGroupsResource.actions.resetErrors}
      root={root}
      renderRowActions={renderRowActions}
      data-aid="admin-keyword-groups-page"
    />
  );
};

export default KeywordGroups;
